import { useMemo, useRef, useState } from 'react';

/**
 * Line chart over time, with a crosshair and tooltip.
 *
 * Inline SVG rather than a charting library: the whole requirement is a few
 * hundred points on one axis, and a library would add a bundle, a theming layer
 * that has to be taught the brand tokens twice, and its own idea of what a
 * tooltip looks like.
 *
 * Rules this deliberately follows:
 *  - ONE y-axis. Two measures of different scale get two charts, never a second
 *    scale on the right — a dual axis lets the author choose where the lines
 *    cross, which means the reader learns nothing from where they cross.
 *  - A legend whenever there are two or more series, and direct labels on the
 *    last point. Identity is never carried by color alone: three of the light-mode
 *    series slots sit below 3:1 on the light surface, so labels are obligatory
 *    there, and always having them is simpler than remembering when.
 *  - A table view underneath, toggleable. It is the accessible reading of the same
 *    data and it satisfies the contrast relief rule.
 *  - Recessive grid, 2px marks, no point markers on dense series.
 */

export interface Series {
  label: string;
  /** CSS custom property name, e.g. 'var(--series-1)'. */
  color: string;
  points: Array<{ t: Date; v: number | null }>;
}

interface Props {
  title: string;
  series: Series[];
  /** Fixed upper bound, e.g. 100 for a percentage. Omit to scale to the data. */
  max?: number;
  unit?: string;
  height?: number;
  format?: (v: number) => string;
}

const PAD = { top: 12, right: 16, bottom: 26, left: 48 };

export function TimeSeries({ title, series, max, unit = '', height = 200, format }: Props) {
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);
  const [showTable, setShowTable] = useState(false);
  const svgRef = useRef<SVGSVGElement>(null);

  const width = 720; // viewBox width; the SVG scales to its container
  const plotW = width - PAD.left - PAD.right;
  const plotH = height - PAD.top - PAD.bottom;

  const fmt = format ?? ((v: number) => `${Math.round(v * 10) / 10}${unit}`);

  const { times, yMax, paths, lastPoints } = useMemo(() => {
    const times = series[0]?.points.map((p) => p.t) ?? [];
    const values = series.flatMap((s) => s.points.map((p) => p.v).filter((v): v is number => v !== null));
    const dataMax = values.length ? Math.max(...values) : 1;
    // Headroom so the peak is not welded to the top edge.
    const yMax = max ?? Math.max(1, dataMax * 1.15);

    const x = (i: number) => PAD.left + (times.length > 1 ? (i / (times.length - 1)) * plotW : plotW / 2);
    const y = (v: number) => PAD.top + plotH - (Math.min(v, yMax) / yMax) * plotH;

    const paths = series.map((s) => {
      // A null breaks the line rather than interpolating across it: a gap in
      // collection is a fact about the estate, and drawing through it invents
      // data for a window where the host was unreachable.
      let d = '';
      let pen = false;
      s.points.forEach((p, i) => {
        if (p.v === null) {
          pen = false;
          return;
        }
        d += `${pen ? 'L' : 'M'}${x(i).toFixed(1)},${y(p.v).toFixed(1)}`;
        pen = true;
      });
      return { ...s, d };
    });

    const lastPoints = series.map((s) => {
      for (let i = s.points.length - 1; i >= 0; i--) {
        const p = s.points[i];
        if (p.v !== null) return { label: s.label, color: s.color, x: x(i), y: y(p.v), v: p.v };
      }
      return null;
    });

    return { times, yMax, paths, lastPoints };
  }, [series, max, plotW, plotH]);

  if (!times.length) {
    return (
      <figure className="chart">
        <figcaption className="chart-title">{title}</figcaption>
        <p className="chart-empty">No samples in this window yet.</p>
      </figure>
    );
  }

  const xAt = (i: number) => PAD.left + (times.length > 1 ? (i / (times.length - 1)) * plotW : plotW / 2);
  const gridLines = [0, 0.25, 0.5, 0.75, 1];

  const onMove = (e: React.PointerEvent<SVGSVGElement>) => {
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect) return;
    const rel = ((e.clientX - rect.left) / rect.width) * width;
    const i = Math.round(((rel - PAD.left) / plotW) * (times.length - 1));
    setHoverIndex(Math.max(0, Math.min(times.length - 1, i)));
  };

  return (
    <figure className="chart">
      <figcaption className="chart-head">
        <span className="chart-title">{title}</span>
        <button type="button" className="chart-toggle" onClick={() => setShowTable((s) => !s)}>
          {showTable ? 'Chart' : 'Table'}
        </button>
      </figcaption>

      {series.length > 1 && (
        <ul className="chart-legend">
          {series.map((s) => (
            <li key={s.label}>
              <span className="swatch" style={{ background: s.color }} aria-hidden="true" />
              {s.label}
            </li>
          ))}
        </ul>
      )}

      {showTable ? (
        <div className="table-scroll">
          <table className="data-table">
            <thead>
              <tr>
                <th>Time</th>
                {series.map((s) => (
                  <th key={s.label} className="num">{s.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {times.map((t, i) => (
                <tr key={t.toISOString()}>
                  <td>{t.toLocaleString()}</td>
                  {series.map((s) => (
                    <td key={s.label} className="num">
                      {s.points[i]?.v === null || s.points[i]?.v === undefined ? '—' : fmt(s.points[i].v!)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <svg
          ref={svgRef}
          viewBox={`0 0 ${width} ${height}`}
          className="chart-svg"
          role="img"
          aria-label={`${title}. ${series.map((s) => s.label).join(', ')}. Switch to the table view for exact values.`}
          onPointerMove={onMove}
          onPointerLeave={() => setHoverIndex(null)}
        >
          {gridLines.map((g) => {
            const y = PAD.top + plotH - g * plotH;
            return (
              <g key={g}>
                <line x1={PAD.left} x2={width - PAD.right} y1={y} y2={y} className="grid" />
                <text x={PAD.left - 8} y={y + 4} className="axis-label" textAnchor="end">
                  {fmt(yMax * g)}
                </text>
              </g>
            );
          })}

          {/* x labels at the ends only: a dense axis of timestamps collides and
              adds nothing the tooltip does not answer better. */}
          <text x={PAD.left} y={height - 8} className="axis-label" textAnchor="start">
            {times[0].toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </text>
          <text x={width - PAD.right} y={height - 8} className="axis-label" textAnchor="end">
            {times[times.length - 1].toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </text>

          {paths.map((p) => (
            <path key={p.label} d={p.d} fill="none" stroke={p.color} strokeWidth={2}
              strokeLinecap="round" strokeLinejoin="round" />
          ))}

          {/* Direct label on the final value of each series. */}
          {lastPoints.map(
            (lp) =>
              lp && (
                <circle key={lp.label} cx={lp.x} cy={lp.y} r={3.5} fill={lp.color}
                  stroke="var(--chart-surface)" strokeWidth={2} />
              ),
          )}

          {hoverIndex !== null && (
            <g>
              <line x1={xAt(hoverIndex)} x2={xAt(hoverIndex)} y1={PAD.top} y2={PAD.top + plotH}
                className="crosshair" />
              {series.map((s) => {
                const v = s.points[hoverIndex]?.v;
                if (v === null || v === undefined) return null;
                const cy = PAD.top + plotH - (Math.min(v, yMax) / yMax) * plotH;
                return (
                  <circle key={s.label} cx={xAt(hoverIndex)} cy={cy} r={4} fill={s.color}
                    stroke="var(--chart-surface)" strokeWidth={2} />
                );
              })}
            </g>
          )}
        </svg>
      )}

      {hoverIndex !== null && !showTable && (
        <div className="chart-tooltip" role="status">
          <strong>{times[hoverIndex].toLocaleString()}</strong>
          {series.map((s) => (
            <span key={s.label}>
              <span className="swatch" style={{ background: s.color }} aria-hidden="true" />
              {s.label}: {s.points[hoverIndex]?.v === null || s.points[hoverIndex]?.v === undefined
                ? '—'
                : fmt(s.points[hoverIndex].v!)}
            </span>
          ))}
        </div>
      )}
    </figure>
  );
}
